import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { KnowledgeNode } from "../types"
import {
  getOriginalDownloadFileName,
  isUploadedFileNode,
} from "../knowledgeBaseDownload"
import {
  useCreateWebsiteCrawlApi,
  type FileStatusItemSchema,
  connectionResponseToConnection,
  useAddKnowledgeBaseFilesFromGoogleDriveByKbIdApi,
  useAddKnowledgeBaseFilesFromNotionByKbIdApi,
  useCreateKnowledgeBaseNodeApi,
  useDeleteKnowledgeBaseNodeApi,
  useGetConnectionsApi,
  useGetOrganizationKnowledgeBaseStorageApi,
  useKnowledgeBaseFileContentByKbIdApi,
  useKnowledgeBaseFileStatusByWorkspaceApi,
  useKnowledgeBaseTabularCapabilitiesApi,
  useListKnowledgeBaseNodesApi,
  organizationKnowledgeBaseStorageResponseToStorage,
  useUpdateKnowledgeBaseNodeApi,
  useUploadKnowledgeBaseFilesByKbIdApi,
} from "@/hooks/api"
import type { ConnectionResponse } from "@/hooks/api/connections"
import { useStore } from "@/store"
import { Connection } from "@/types"
import { useGoogleDrive } from "./useGoogleDrive"
import { useNotion } from "./useNotion"
import { getLimitErrorTranslationKey } from "@/constants/errors"
import {
  getTabularFileValidationFailure,
  isFileTypeSupported,
  MAX_KB_UPLOAD_FILES,
  normalizeMarkdownType,
  SUPPORTED_KB_MIME_TYPES,
} from "@/lib/fileUtils"
import { useToast } from "@/hooks"
import { useTranslation } from "react-i18next"
import ky from "ky"
import JSZip from "jszip"
import { saveAs } from "file-saver"
import useSysConfig from "@/hooks/useSysConfig"
import { API_PREFIX } from "@/constants"
import {
  getApiErrorData,
  getUserFacingApiErrorMessage,
  getUserFacingApiErrorToastProps,
} from "@/lib/apiErrors"
import {
  applyKnowledgeBaseFileStatusUpdates,
  advanceKnowledgeBasePollingScheduler,
  collectResolvedPendingKnowledgeBaseNodeIds,
  collectKnowledgeBaseNodeIds,
  collectPendingKnowledgeBaseNodeIdsByWorkspace,
  createKnowledgeBasePollingSchedulerState,
  getKnowledgeBasePollingDelayMs,
  hasPendingKnowledgeBaseStatus,
  type KnowledgeBaseNodeStatusIndex,
  removePendingKnowledgeBaseNodes,
  resolveKnowledgeBasePollingChunkProgress,
  resolveKnowledgeBasePollingOutcome,
  WORKSPACE_FILE_STATUS_BATCH_SIZE,
} from "./knowledgeBasePolling"
import { KnowledgeBasePollingVisibilityController } from "./knowledgeBaseVisibility"
import {
  buildKnowledgeBaseNodeMediaUrl,
  isImageNode,
} from "../knowledgeBaseImage"

const KB_MIME_BY_EXTENSION: Record<string, string> = {
  ".pdf": "application/pdf",
  ".txt": "text/plain",
  ".md": "text/markdown",
  ".csv": "text/csv",
  ".doc": "application/msword",
  ".docx":
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".pptx":
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ".html": "text/html",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
  ".gif": "image/gif",
  ".webp": "image/webp",
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
}

const normalizeKnowledgeBaseUploadFile = (
  file: File,
  allowedMimeTypes: readonly string[] = SUPPORTED_KB_MIME_TYPES
): File | null => {
  const normalizedFile = normalizeMarkdownType(file)
  if (isFileTypeSupported(normalizedFile, allowedMimeTypes)) {
    return normalizedFile
  }
  const extensionIndex = file.name.lastIndexOf(".")
  if (extensionIndex < 0) return null
  const canonicalMimeType =
    KB_MIME_BY_EXTENSION[file.name.slice(extensionIndex).toLowerCase()]
  if (!canonicalMimeType) return null

  // Browser MIME metadata is inconsistent; normalize supported Knowledge Base
  // extensions before upload while keeping backend validation authoritative.
  const canonicalFile = new File([file], file.name, {
    type: canonicalMimeType,
    lastModified: file.lastModified,
  })
  return isFileTypeSupported(canonicalFile, allowedMimeTypes)
    ? canonicalFile
    : null
}

type KnowledgeBaseStorageSummary = {
  bytesUsed: number
  bytesLimit: number | null
  bytesRemaining: number | null
  atLimit: boolean
}

export interface TreeNodeState {
  [nodeId: string]: boolean // true = expanded
}

export interface UseKnowledgeBaseReturn {
  // === Core tree state ===
  root: KnowledgeNode
  totalItems: number

  // === Persistent UI state (shared across view types) ===
  selectedNode: KnowledgeNode | null

  // === Persistent selection ===
  selectNode: (node: KnowledgeNode | null) => void

  // === Backend mutations ===
  createFolder: (
    parentFolderId: string,
    name: string,
    description?: string | null
  ) => Promise<void>
  createWebsiteCrawl: (
    workspaceNodeId: string,
    payload: {
      startUrl: string
      crawlConfig?: Record<string, unknown>
    }
  ) => Promise<KnowledgeNode | null>
  deleteNode: (nodeId: string) => Promise<void>
  updateNode: (
    nodeId: string,
    patch: { name?: string; description?: string | null }
  ) => Promise<void>
  addFiles: (folderId: string, files: File[]) => Promise<void>
  addFromGoogleDrive: (folderId: string, connectionId: string) => Promise<void>
  connectAnotherGoogleAccount: (folderId: string) => Promise<void>
  addFromNotionConnection: (
    folderId: string,
    connectionId: string
  ) => Promise<void>
  connectAnotherNotionWorkspace: (folderId: string) => Promise<void>
  deleteConnection: (connectionId: string) => Promise<void>
  connections: Connection[]
  knowledgeBaseStorage: KnowledgeBaseStorageSummary | null
  isKbStorageAtLimit: boolean
  isKbStorageAtLimitForWorkspaceId: (
    workspaceId: string | null | undefined
  ) => boolean

  // === Workspace / KB ===
  workspaces: Array<{ id: string; name: string }>

  // === Lazy loading ===
  ensureChildrenLoaded: (folderId: string) => Promise<KnowledgeNode[] | null>
  refreshFolderChildren: (folderId: string) => Promise<KnowledgeNode[] | null>

  // === File content ===
  fetchFileContent: (nodeId: string) => Promise<void>
  getFileMediaUrl: (node: KnowledgeNode) => string | null
  downloadFile: (node: KnowledgeNode) => Promise<void>
  downloadFilesAsZip: (nodes: KnowledgeNode[]) => Promise<void>
}

interface WorkspacePollRuntime {
  inFlight: boolean
  timeoutId: number | null
  schedulerState: ReturnType<typeof createKnowledgeBasePollingSchedulerState>
}

const resolveFileDownloadMeta = (node: KnowledgeNode, rawFileName: string) => {
  // External sources expose converted text, never the source file's binary format.
  const isPlainText = node.content_type?.startsWith("text/plain")
  const fileExtension = isPlainText ? ".txt" : ".md"
  const mimeType = isPlainText
    ? "text/plain;charset=utf-8"
    : "text/markdown;charset=utf-8"

  if (isUploadedFileNode(node)) {
    const fileName = rawFileName.replace(/[\\/]/g, "_")
    const extensionIndex = fileName.lastIndexOf(".")
    return {
      safeBaseName:
        extensionIndex > 0 ? fileName.slice(0, extensionIndex) : fileName,
      fileExtension: extensionIndex > 0 ? fileName.slice(extensionIndex) : "",
      mimeType: node.content_type || "application/octet-stream",
    }
  }

  let safeBaseName = rawFileName
    .replace(/[^\p{L}\p{N}\s.-]/gu, "")
    .replace(/\s+/g, "_")
    .replace(/\.[^/.]+$/, "")
    .substring(0, 100)

  if (!safeBaseName || safeBaseName.trim().length === 0) {
    safeBaseName = "file"
  }

  return { safeBaseName, fileExtension, mimeType }
}

const buildZipFileName = () => {
  const now = new Date()
  const pad2 = (value: number) => value.toString().padStart(2, "0")
  return `knowledge-base-${now.getFullYear()}${pad2(now.getMonth() + 1)}${pad2(now.getDate())}-${pad2(now.getHours())}${pad2(now.getMinutes())}${pad2(now.getSeconds())}.zip`
}

/**
 * useKnowledgeBase (backend-integrated)
 *
 * Shared Knowledge Base controller hook:
 * - Owns all data state + handlers so different views (Tree/Grid/...) can switch
 *   without losing state.
 * - Views should be mostly presentational and consume this hook’s return value.
 */
const UI_ROOT_ID = "kb-explorer:root"
const workspaceNodeId = (workspaceId: string) =>
  `kb-explorer:workspace:${workspaceId}`

function isWorkspaceNode(node: KnowledgeNode | null | undefined): boolean {
  return node?.ui_kind === "WORKSPACE"
}

function getKbIdForNode(node: KnowledgeNode | null | undefined): string | null {
  if (!node) return null
  if (isWorkspaceNode(node)) return null
  if ((node.knowledge_base_id || "").startsWith("kb-explorer:")) return null
  return node.knowledge_base_id || null
}

function writeKnowledgeBaseNodeStatusesToIndex(
  index: KnowledgeBaseNodeStatusIndex,
  nodes: KnowledgeNode[]
): void {
  const visit = (node: KnowledgeNode) => {
    index.set(node.id, node.status)

    for (const child of node.children || []) {
      visit(child)
    }
  }

  for (const node of nodes) {
    visit(node)
  }
}

function removeKnowledgeBaseNodeStatusesFromIndex(
  index: KnowledgeBaseNodeStatusIndex,
  nodeIds: string[]
): void {
  for (const nodeId of nodeIds) {
    index.delete(nodeId)
  }
}

/**
 * The Knowledge Base explorer uses a hierarchical tree structure with three layers:
 *
 * Example tree:
 * ```
 * root (synthetic, frontend only)
 * ├── workspace1 (synthetic, fontend only)
 * │   ├── KB1 (real, from DB)
 * │   │   ├── folder1 (real, from DB)
 * │   │   │   └── file1.pdf (real, from DB)
 * │   │   └── file2.docx (real, from DB)
 * │   └── KB2 (real, from DB)
 * │       └── folder2 (real, from DB)
 * └── workspace2 (synthetic, fontend only)
 *     └── KB3 (real, from DB)
 * ```
 *
 * Key Design Decisions:
 * - Synthetic root allows grouping KBs by workspace without backend changes
 * - Workspace nodes are synthetic to avoid backend dependency on workspace hierarchy
 * - All real nodes (KB roots, folders, files) come from backend API
 * - Lazy loading: Children are only fetched when parent is expanded
 * - Tree is maintained in-memory via `root` state, updated optimistically on mutations
 */
export function useKnowledgeBase(): UseKnowledgeBaseReturn {
  const { alertToast, successToast, toast } = useToast()
  const { t } = useTranslation()
  const [storeWorkspaces, currentOrganization] = useStore((s) => [
    s.workspaces,
    s.currentOrganization,
  ])

  // Note on org scoping:
  // In the current app flow, DataLoader repopulates `storeWorkspaces` for the
  // active `currentOrganization` during navigation, so this page normally behaves
  // as current-organization scoped. We still key KB storage summaries by each
  // workspace's `organizationId` here as a defensive guard, in case future store
  // behavior ever allows mixed-organization workspaces to coexist.
  const workspaceOrgIdMap = useMemo(() => {
    const m = new Map<string, string>()
    for (const w of storeWorkspaces || []) {
      if (w.organizationId) m.set(w.id, w.organizationId)
    }
    return m
  }, [storeWorkspaces])

  const getOrganizationIdForWorkspaceId = useCallback(
    (workspaceId: string): string | null => {
      return (
        workspaceOrgIdMap.get(workspaceId) || (currentOrganization?.id ?? null)
      )
    },
    [currentOrganization?.id, workspaceOrgIdMap]
  )

  const workspaces = useMemo(() => {
    return (storeWorkspaces || [])
      .filter((w) => w.isJoined)
      .map((w) => ({ id: w.id, name: w.name }))
  }, [storeWorkspaces])

  // Initialize the synthetic root node (see tree structure design comment above).
  // This root contains workspace nodes as children, which in turn contain KB nodes.
  const [root, setRoot] = useState<KnowledgeNode>(() => ({
    id: UI_ROOT_ID,
    knowledge_base_id: UI_ROOT_ID,
    parent_id: null,
    node_type: "FOLDER",
    name: "Home",
    description: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    children: [],
    children_loaded: true,
    ui_kind: "NODE" as const,
  }))

  // === Persistent UI state ===
  // Default selection should be the synthetic root so the right panel is never empty.
  const [selectedNodeId, setSelectedNodeId] = useState<string>(UI_ROOT_ID)

  // === Tree helpers ===
  const findNodeById = useCallback(
    (node: KnowledgeNode, id: string): KnowledgeNode | null => {
      if (node.id === id) return node
      if (!node.children) return null
      for (const child of node.children) {
        const found = findNodeById(child, id)
        if (found) return found
      }
      return null
    },
    []
  )

  const findPathToNode = useCallback(
    (node: KnowledgeNode, targetId: string): KnowledgeNode[] | null => {
      if (node.id === targetId) return [node]
      if (!node.children) return null
      for (const child of node.children) {
        const childPath = findPathToNode(child, targetId)
        if (childPath) return [node, ...childPath]
      }
      return null
    },
    []
  )

  const updateNodeInTree = useCallback(
    (
      node: KnowledgeNode,
      targetId: string,
      updater: (n: KnowledgeNode) => KnowledgeNode | null
    ): KnowledgeNode | null => {
      if (node.id === targetId) return updater(node)

      if (!node.children) return node

      const updatedChildren: KnowledgeNode[] = []
      let changed = false
      for (const child of node.children) {
        const updatedChild = updateNodeInTree(child, targetId, updater)
        if (updatedChild === null) {
          changed = true // child deleted
          continue
        }
        if (updatedChild !== child) changed = true
        updatedChildren.push(updatedChild)
      }

      if (!changed) return node
      return { ...node, children: updatedChildren }
    },
    []
  )

  // Sync workspace nodes from store into the explorer root.
  useEffect(() => {
    setRoot((prev) => {
      const existingChildren = prev.children || []
      const byWorkspaceId = new Map<string, KnowledgeNode>()
      for (const c of existingChildren) {
        if (isWorkspaceNode(c) && c.workspace_id)
          byWorkspaceId.set(c.workspace_id, c)
      }

      const nowIso = new Date().toISOString()
      // Synthesize workspace node
      const workspaceNodes: KnowledgeNode[] = workspaces.map((w) => {
        const existing = byWorkspaceId.get(w.id)
        if (existing) return { ...existing, name: w.name }
        return {
          id: workspaceNodeId(w.id),
          knowledge_base_id: workspaceNodeId(w.id),
          parent_id: UI_ROOT_ID,
          node_type: "FOLDER",
          name: w.name,
          description: null,
          created_at: nowIso,
          updated_at: nowIso,
          children: [],
          children_loaded: false,
          ui_kind: "WORKSPACE" as const,
          workspace_id: w.id,
        }
      })

      return { ...prev, children: workspaceNodes, children_loaded: true }
    })
  }, [workspaces])

  const selectedNode = useMemo(() => {
    return findNodeById(root, selectedNodeId)
  }, [findNodeById, root, selectedNodeId])

  const countItems = useCallback((node: KnowledgeNode): number => {
    if (node.node_type === "FILE") return 1
    return (
      1 +
      (node.children?.reduce((sum, child) => sum + countItems(child), 0) || 0)
    )
  }, [])

  const totalItems = useMemo(
    () => Math.max(0, countItems(root) - 1),
    [countItems, root]
  )

  const selectNode = useCallback((node: KnowledgeNode | null) => {
    setSelectedNodeId(node?.id ?? UI_ROOT_ID)
  }, [])

  // === Backend APIs (dynamic) ===
  const { fetchFn: fetchKnowledgeBaseStorage } =
    useGetOrganizationKnowledgeBaseStorageApi()
  const {
    error: listNodesError,
    data: listNodesResp,
    fetchFn: listNodesFetchFn,
  } = useListKnowledgeBaseNodesApi()
  const {
    error: createNodeError,
    data: createNodeResp,
    fetchFn: createNodeFetchFn,
  } = useCreateKnowledgeBaseNodeApi()
  const {
    error: updateNodeError,
    data: updateNodeResp,
    fetchFn: updateNodeFetchFn,
  } = useUpdateKnowledgeBaseNodeApi()
  const {
    error: uploadError,
    data: uploadResp,
    fetchFn: uploadFetchFn,
  } = useUploadKnowledgeBaseFilesByKbIdApi()
  const { fetchFn: getTabularCapabilitiesFetchFn } =
    useKnowledgeBaseTabularCapabilitiesApi()
  const {
    error: addFromDriveError,
    data: addFromDriveResp,
    fetchFn: addFromDriveFetchFn,
  } = useAddKnowledgeBaseFilesFromGoogleDriveByKbIdApi()
  const {
    error: addFromNotionError,
    data: addFromNotionResp,
    fetchFn: addFromNotionFetchFn,
  } = useAddKnowledgeBaseFilesFromNotionByKbIdApi()
  const {
    error: fileStatusError,
    data: fileStatusResp,
    fetchFn: fileStatusFetchFn,
  } = useKnowledgeBaseFileStatusByWorkspaceApi()
  const {
    error: fileContentError,
    data: fileContentResp,
    fetchFn: fileContentFetchFn,
  } = useKnowledgeBaseFileContentByKbIdApi()
  const {
    error: deleteNodeError,
    data: deleteNodeResp,
    fetchFn: deleteNodeFetchFn,
  } = useDeleteKnowledgeBaseNodeApi()
  const {
    error: createWebsiteCrawlError,
    data: createWebsiteCrawlResp,
    fetchFn: createWebsiteCrawlFetchFn,
  } = useCreateWebsiteCrawlApi()
  // Manage connections at the useKnowledgeBase level
  const {
    error: getConnectionsError,
    data: getConnectionsResp,
    fetchFn: getConnections,
  } = useGetConnectionsApi()

  const sys = useSysConfig()

  const getFileMediaUrl = useCallback(
    (node: KnowledgeNode): string | null => {
      if (!isImageNode(node)) return null
      const workspaceId = node.workspace_id
      const knowledgeBaseId = getKbIdForNode(node)
      const organizationId = workspaceId
        ? getOrganizationIdForWorkspaceId(workspaceId)
        : null
      if (
        !sys.serverRoot ||
        !organizationId ||
        !workspaceId ||
        !knowledgeBaseId
      ) {
        return null
      }

      return buildKnowledgeBaseNodeMediaUrl({
        serverRoot: sys.serverRoot,
        organizationId,
        workspaceId,
        knowledgeBaseId,
        nodeId: node.id,
      })
    },
    [getOrganizationIdForWorkspaceId, sys.serverRoot]
  )
  const [knowledgeBaseStorageByOrgId, setKnowledgeBaseStorageByOrgId] =
    useState<Record<string, KnowledgeBaseStorageSummary>>({})
  const joinedOrganizationIds = useMemo(
    () =>
      Array.from(
        new Set(
          (storeWorkspaces || [])
            .filter((w) => w.isJoined && w.organizationId)
            .map((w) => w.organizationId as string)
        )
      ),
    [storeWorkspaces]
  )

  const fetchKbStorageSummaryByOrganizationId = useCallback(
    async (organizationId: string) => {
      if (!organizationId) return null
      console.debug("[KB storage] fetch summary start", {
        organizationId,
      })
      try {
        const resp = await fetchKnowledgeBaseStorage({
          urlBuilderParams: { organizationId },
          preserveData: true,
        })
        const summary = resp?.data
          ? organizationKnowledgeBaseStorageResponseToStorage(resp.data)
          : null
        if (summary) {
          console.debug("[KB storage] fetch summary success", {
            organizationId,
            summary,
          })
          setKnowledgeBaseStorageByOrgId((prev) => ({
            ...prev,
            [organizationId]: summary,
          }))
        }
        return summary
      } catch (error) {
        console.error("[KB storage] fetch summary failed", {
          organizationId,
          error,
        })
        return null
      }
    },
    [fetchKnowledgeBaseStorage]
  )

  const refreshKbStorageSummary = useCallback(() => {
    console.debug("[KB storage] refresh summaries", {
      joinedOrganizationIds,
    })
    void Promise.all(
      joinedOrganizationIds.map((orgId) =>
        fetchKbStorageSummaryByOrganizationId(orgId)
      )
    )
  }, [fetchKbStorageSummaryByOrganizationId, joinedOrganizationIds])

  useEffect(() => {
    console.debug("[KB storage] refresh effect fired")
    refreshKbStorageSummary()
  }, [refreshKbStorageSummary])

  const activeOrganizationId = useMemo(() => {
    const workspaceId = selectedNode?.workspace_id
    if (workspaceId) return getOrganizationIdForWorkspaceId(workspaceId)
    return currentOrganization?.id ?? null
  }, [
    currentOrganization?.id,
    getOrganizationIdForWorkspaceId,
    selectedNode?.workspace_id,
  ])

  const knowledgeBaseStorage = activeOrganizationId
    ? knowledgeBaseStorageByOrgId[activeOrganizationId] || null
    : null
  const isKbStorageAtLimit = knowledgeBaseStorage?.atLimit ?? false

  useEffect(() => {
    console.debug("[KB storage] active summary changed", {
      activeOrganizationId,
      knowledgeBaseStorage,
      selectedNodeId,
    })
  }, [activeOrganizationId, knowledgeBaseStorage, selectedNodeId])

  const isKbStorageAtLimitForWorkspaceId = useCallback(
    (workspaceId: string | null | undefined) => {
      if (!workspaceId) return false
      const organizationId = getOrganizationIdForWorkspaceId(workspaceId)
      if (!organizationId) return false
      return knowledgeBaseStorageByOrgId[organizationId]?.atLimit ?? false
    },
    [getOrganizationIdForWorkspaceId, knowledgeBaseStorageByOrgId]
  )

  const showKbStorageLimitReached = useCallback(() => {
    alertToast({
      title: t("knowledgeBase.treeView.addFile"),
      description: t("knowledgeBase.treeView.storageLimitReached"),
    })
  }, [alertToast, t])

  useEffect(() => {
    if (!listNodesError) return
    console.error("List nodes API error:", listNodesError)
    alertToast({
      title: t("constants.errors.somethingWrong"),
      description: getUserFacingApiErrorMessage(
        listNodesError,
        t,
        (listNodesResp as { message?: string } | undefined)?.message ||
          t("constants.errors.somethingWrong")
      ),
    })
  }, [listNodesError, listNodesResp, alertToast, t])

  useEffect(() => {
    if (!createNodeError) return
    console.error("Create node API error:", createNodeError)
    alertToast({
      title: t("knowledgeBase.treeView.newFolderTitle"),
      description: getUserFacingApiErrorMessage(
        createNodeError,
        t,
        (createNodeResp as { message?: string } | undefined)?.message ||
          t("constants.errors.somethingWrong")
      ),
    })
  }, [createNodeError, createNodeResp, alertToast, t])

  useEffect(() => {
    if (!updateNodeError) return
    console.error("Update node API error:", updateNodeError)
    alertToast({
      title: t("knowledgeBase.treeView.updateNodeTitle"),
      description: getUserFacingApiErrorMessage(
        updateNodeError,
        t,
        (updateNodeResp as { message?: string } | undefined)?.message ||
          t("constants.errors.somethingWrong")
      ),
    })
  }, [updateNodeError, updateNodeResp, alertToast, t])

  useEffect(() => {
    if (!uploadError) return
    console.error("Upload API error:", uploadError)
    const errorData = getApiErrorData(uploadError)
    const limitErrorData = errorData || uploadResp
    const limitKey = getLimitErrorTranslationKey(
      (limitErrorData as { error_code?: number } | null | undefined)?.error_code
    )
    alertToast({
      title: t("knowledgeBase.treeView.uploadFile"),
      description: limitKey
        ? t(limitKey)
        : getUserFacingApiErrorMessage(
            uploadError,
            t,
            (uploadResp as { message?: string } | undefined)?.message ||
              t("constants.errors.somethingWrong")
          ),
    })
  }, [uploadError, uploadResp, alertToast, t])

  useEffect(() => {
    if (!addFromDriveError) return
    console.error("Add from Google Drive API error:", addFromDriveError)
    const errorData = getApiErrorData(addFromDriveError)
    const limitErrorData = errorData || addFromDriveResp
    const limitKey = getLimitErrorTranslationKey(
      (limitErrorData as { error_code?: number } | null | undefined)?.error_code
    )
    alertToast({
      title: t("knowledgeBase.treeView.addFromConnection"),
      description: limitKey
        ? t(limitKey)
        : getUserFacingApiErrorMessage(
            addFromDriveError,
            t,
            (addFromDriveResp as { message?: string } | undefined)?.message ||
              t("constants.errors.somethingWrong")
          ),
    })
  }, [addFromDriveError, addFromDriveResp, alertToast, t])

  useEffect(() => {
    if (!addFromNotionError) return
    console.error("Add from Notion API error:", addFromNotionError)
    const errorData = getApiErrorData(addFromNotionError)
    const limitErrorData = errorData || addFromNotionResp
    const limitKey = getLimitErrorTranslationKey(
      (limitErrorData as { error_code?: number } | null | undefined)?.error_code
    )
    alertToast({
      title: t("knowledgeBase.treeView.addFromConnection"),
      description: limitKey
        ? t(limitKey)
        : getUserFacingApiErrorMessage(
            addFromNotionError,
            t,
            (addFromNotionResp as { message?: string } | undefined)?.message ||
              t("constants.errors.somethingWrong")
          ),
    })
  }, [addFromNotionError, addFromNotionResp, alertToast, t])

  useEffect(() => {
    if (!fileStatusError) return
    console.error("File status API error:", fileStatusError)
    alertToast({
      title: t("constants.errors.somethingWrong"),
      description: getUserFacingApiErrorMessage(
        fileStatusError,
        t,
        (fileStatusResp as { message?: string } | undefined)?.message ||
          t("constants.errors.somethingWrong")
      ),
    })
  }, [fileStatusError, fileStatusResp, alertToast, t])

  useEffect(() => {
    if (!fileContentError) return
    console.error("File content API error:", fileContentError)
    alertToast({
      title: t("knowledgeBase.treeView.contents"),
      description: getUserFacingApiErrorMessage(
        fileContentError,
        t,
        (fileContentResp as { message?: string } | undefined)?.message ||
          t("constants.errors.somethingWrong")
      ),
    })
  }, [fileContentError, fileContentResp, alertToast, t])

  useEffect(() => {
    if (!deleteNodeError) return
    console.error("Delete node API error:", deleteNodeError)
    alertToast({
      title: t("knowledgeBase.treeView.deleteTitle"),
      description: getUserFacingApiErrorMessage(
        deleteNodeError,
        t,
        (deleteNodeResp as { message?: string } | undefined)?.message ||
          t("constants.errors.somethingWrong")
      ),
    })
  }, [deleteNodeError, deleteNodeResp, alertToast, t])

  useEffect(() => {
    if (!getConnectionsError) return
    console.error("Get connections API error:", getConnectionsError)
    alertToast({
      title: t("constants.errors.somethingWrong"),
      description: getUserFacingApiErrorMessage(
        getConnectionsError,
        t,
        (getConnectionsResp as { message?: string } | undefined)?.message ||
          t("constants.errors.somethingWrong")
      ),
    })
  }, [getConnectionsError, getConnectionsResp, alertToast, t])

  // node_id -> workspace/kb metadata for status polling
  const [pendingNodeMap, setPendingNodeMap] = useState<
    Map<string, { kbId: string; workspaceId: string }>
  >(() => new Map())
  const nodeStatusIndexRef = useRef<KnowledgeBaseNodeStatusIndex>(new Map())
  const contentInFlightRef = useRef<Set<string>>(new Set())
  const pendingNodeIdsByWorkspaceRef = useRef<Map<string, string[]>>(new Map())
  const previousPendingNodeIdsByWorkspaceRef = useRef<Map<string, Set<string>>>(
    new Map()
  )
  const workspacePollRuntimesRef = useRef<Map<string, WorkspacePollRuntime>>(
    new Map()
  )
  const pollingVisibilityControllerRef =
    useRef<KnowledgeBasePollingVisibilityController | null>(null)
  const runPendingNodePollCycleRef = useRef<
    (workspaceId: string) => Promise<void>
  >(async () => {})

  const getWorkspacePollRuntime = useCallback((workspaceId: string) => {
    const existing = workspacePollRuntimesRef.current.get(workspaceId)
    if (existing) return existing

    const runtime: WorkspacePollRuntime = {
      inFlight: false,
      timeoutId: null,
      schedulerState: createKnowledgeBasePollingSchedulerState(),
    }
    workspacePollRuntimesRef.current.set(workspaceId, runtime)
    return runtime
  }, [])

  const clearPendingNodePollTimeout = useCallback((workspaceId: string) => {
    const runtime = workspacePollRuntimesRef.current.get(workspaceId)
    if (!runtime?.timeoutId) return

    window.clearTimeout(runtime.timeoutId)
    runtime.timeoutId = null
  }, [])

  const clearAllPendingNodePollTimeouts = useCallback(() => {
    for (const runtime of workspacePollRuntimesRef.current.values()) {
      if (runtime.timeoutId === null) continue
      window.clearTimeout(runtime.timeoutId)
      runtime.timeoutId = null
    }
  }, [])

  const removeWorkspacePollRuntime = useCallback(
    (workspaceId: string) => {
      clearPendingNodePollTimeout(workspaceId)
      workspacePollRuntimesRef.current.delete(workspaceId)
    },
    [clearPendingNodePollTimeout]
  )

  const canPollWorkspace = useCallback((_workspaceId: string) => {
    const controller = pollingVisibilityControllerRef.current
    if (!controller) {
      return (
        typeof document === "undefined" ||
        document.visibilityState === "visible"
      )
    }

    return controller.canPoll()
  }, [])

  const schedulePendingNodePoll = useCallback(
    (workspaceId: string, delayMs: number) => {
      if (!canPollWorkspace(workspaceId)) {
        clearPendingNodePollTimeout(workspaceId)
        return
      }

      const runtime = getWorkspacePollRuntime(workspaceId)
      clearPendingNodePollTimeout(workspaceId)
      runtime.timeoutId = window.setTimeout(() => {
        runtime.timeoutId = null
        void runPendingNodePollCycleRef.current(workspaceId)
      }, delayMs)
    },
    [canPollWorkspace, clearPendingNodePollTimeout, getWorkspacePollRuntime]
  )

  const ensureWorkspaceChildrenLoaded = useCallback(
    async (workspaceNodeIdStr: string): Promise<KnowledgeNode[] | null> => {
      const wsNode = findNodeById(root, workspaceNodeIdStr)
      if (!wsNode || !isWorkspaceNode(wsNode) || !wsNode.workspace_id)
        return null
      if (wsNode.children_loaded) return wsNode.children || []
      const workspaceId = wsNode.workspace_id
      const organizationId = getOrganizationIdForWorkspaceId(workspaceId)
      if (!organizationId) return null

      // List KB roots (real nodes) for this workspace. KB roots are nodes where parent_id is NULL.
      const resp = await listNodesFetchFn({
        urlBuilderParams: { organizationId, workspaceId },
      })
      const list = resp?.data || []
      const kbRootsWithoutChildren: KnowledgeNode[] = list
        .filter((n) => n.node_type === "FOLDER")
        .map((n) => ({
          ...n,
          parent_id: wsNode.id, // attach under synthetic workspace node
          children: [],
          children_loaded: false,
          ui_kind: "NODE" as const,
          workspace_id: workspaceId,
        }))
      const kbRoots: KnowledgeNode[] = await Promise.all(
        kbRootsWithoutChildren.map(async (kbRoot) => {
          try {
            const childParams = new URLSearchParams({
              parent_id: kbRoot.id,
              view: "flat",
            })
            const childrenResp = await listNodesFetchFn({
              urlBuilderParams: { organizationId, workspaceId },
              searchParams: childParams,
            })
            const rawChildren = childrenResp?.data || []
            const fileChildren = rawChildren
              .filter((c) => c.node_type === "FILE")
              .map((c) => ({
                ...c,
                parent_id: kbRoot.id,
                children: [],
                children_loaded: false,
                ui_kind: "NODE" as const,
                workspace_id: workspaceId,
              }))
            return {
              ...kbRoot,
              children: fileChildren,
              children_loaded: true,
            }
          } catch (_error) {
            return kbRoot
          }
        })
      )

      setRoot((prev) => {
        const next = updateNodeInTree(prev, wsNode.id, (n) => {
          if (!isWorkspaceNode(n)) return n
          return { ...n, children: kbRoots, children_loaded: true }
        })
        return next ?? prev
      })
      return kbRoots
    },
    [
      findNodeById,
      getOrganizationIdForWorkspaceId,
      listNodesFetchFn,
      root,
      updateNodeInTree,
    ]
  )

  const mergeChildren = useCallback(
    (parentId: string, children: KnowledgeNode[]) => {
      setRoot((prev) => {
        const parent = findNodeById(prev, parentId)
        if (!parent || parent.node_type !== "FOLDER") {
          return prev
        }

        const next = updateNodeInTree(prev, parentId, (n) => {
          if (n.node_type !== "FOLDER") return n
          return { ...n, children, children_loaded: true }
        })
        if (!next || next === prev) {
          return prev
        }

        const removedNodeIds = (parent.children || []).flatMap((child) =>
          collectKnowledgeBaseNodeIds(child)
        )
        removeKnowledgeBaseNodeStatusesFromIndex(
          nodeStatusIndexRef.current,
          removedNodeIds
        )
        writeKnowledgeBaseNodeStatusesToIndex(
          nodeStatusIndexRef.current,
          children
        )

        return next
      })
    },
    [findNodeById, updateNodeInTree]
  )

  const connections = useMemo(() => {
    const list = Array.isArray(getConnectionsResp)
      ? getConnectionsResp
      : (getConnectionsResp as unknown as { data?: ConnectionResponse[] })
          ?.data || []
    return list.map(connectionResponseToConnection)
  }, [getConnectionsResp])

  // Refresh connections on mount
  useEffect(() => {
    getConnections({ searchParams: new URLSearchParams({ limit: "9999" }) })
  }, [getConnections])

  // Create refresh function to pass to child hooks
  const refreshConnections = useCallback(() => {
    getConnections({ searchParams: new URLSearchParams({ limit: "9999" }) })
  }, [getConnections])

  const googleDrive = useGoogleDrive({
    root,
    findNodeById,
    mergeChildren,
    setPendingNodeMap,
    getOrganizationIdForWorkspaceId,
    listNodes: listNodesFetchFn,
    addFromDrive: addFromDriveFetchFn,
    connections,
    refreshConnections,
  })

  const notion = useNotion({
    root,
    findNodeById,
    mergeChildren,
    setPendingNodeMap,
    getOrganizationIdForWorkspaceId,
    listNodes: listNodesFetchFn,
    addFromNotion: addFromNotionFetchFn,
    refreshConnections,
  })

  const loadFolderChildren = useCallback(
    async (
      folderId: string,
      options?: { force?: boolean }
    ): Promise<KnowledgeNode[] | null> => {
      try {
        const n = findNodeById(root, folderId)
        if (!n) {
          return null
        }
        if (isWorkspaceNode(n)) {
          return await ensureWorkspaceChildrenLoaded(n.id)
        }
        if (n.node_type !== "FOLDER") {
          return null
        }
        if (!options?.force && n.children_loaded) {
          return n.children || []
        }

        const workspaceId = n.workspace_id
        if (!workspaceId) {
          return null
        }
        const organizationId = getOrganizationIdForWorkspaceId(workspaceId)
        if (!organizationId) {
          return null
        }

        const params = new URLSearchParams({
          parent_id: n.id,
          view: "flat",
        })

        const resp = await listNodesFetchFn({
          urlBuilderParams: { organizationId, workspaceId },
          searchParams: params,
        })
        const children = resp?.data || []
        mergeChildren(
          n.id,
          children.map((c) => ({
            ...c,
            parent_id: n.id, // ensure in-memory parent matches current node
            children: [],
            children_loaded: false,
            ui_kind: "NODE" as const,
            workspace_id: workspaceId,
          }))
        )
        return children.map((c) => ({
          ...c,
          parent_id: n.id,
          children: [],
          children_loaded: false,
          ui_kind: "NODE" as const,
          workspace_id: workspaceId,
        }))
      } catch (e) {
        console.error("[KB] ensureChildrenLoaded failed", e)
        return null
      }
    },
    [
      ensureWorkspaceChildrenLoaded,
      findNodeById,
      getOrganizationIdForWorkspaceId,
      listNodesFetchFn,
      mergeChildren,
      root,
    ]
  )

  const ensureChildrenLoaded = useCallback(
    async (folderId: string) => loadFolderChildren(folderId),
    [loadFolderChildren]
  )

  const refreshFolderChildren = useCallback(
    async (folderId: string) => loadFolderChildren(folderId, { force: true }),
    [loadFolderChildren]
  )

  const createFolder = useCallback(
    async (
      parentFolderId: string,
      name: string,
      description?: string | null
    ) => {
      const parent = findNodeById(root, parentFolderId)
      if (!parent) return
      // UI constraint: folders can only be created directly under a workspace node.
      if (!isWorkspaceNode(parent) || !parent.workspace_id) return
      const workspaceId = parent.workspace_id
      if (!name.trim()) return
      const organizationId = getOrganizationIdForWorkspaceId(workspaceId)
      if (!organizationId) return
      // Creating under a workspace means creating the KB root node (parent_id omitted).
      const resp = await createNodeFetchFn({
        urlBuilderParams: { organizationId, workspaceId },
        jsonBody: {
          name: name.trim(),
          description: description ?? null,
        },
      })
      const created = resp?.data
      if (!created) return
      setRoot((prev) => {
        const next = updateNodeInTree(prev, parentFolderId, (n) => {
          if (!isWorkspaceNode(n)) return n
          const children = [
            ...(n.children || []),
            {
              ...created,
              parent_id: n.id, // attach under workspace synthetic node
              children: [],
              children_loaded: false,
              ui_kind: "NODE" as const,
              workspace_id: workspaceId,
            },
          ]
          return { ...n, children, children_loaded: true }
        })
        return next ?? prev
      })
    },
    [
      createNodeFetchFn,
      findNodeById,
      getOrganizationIdForWorkspaceId,
      root,
      updateNodeInTree,
    ]
  )

  const createWebsiteCrawl = useCallback(
    async (
      workspaceNodeIdStr: string,
      payload: {
        startUrl: string
        crawlConfig?: Record<string, unknown>
      }
    ): Promise<KnowledgeNode | null> => {
      const workspaceNode = findNodeById(root, workspaceNodeIdStr)
      if (!workspaceNode || !isWorkspaceNode(workspaceNode)) return null
      if (!workspaceNode.workspace_id) return null

      const workspaceId = workspaceNode.workspace_id
      const organizationId = getOrganizationIdForWorkspaceId(workspaceId)
      if (!organizationId) return null

      const startUrl = payload.startUrl.trim()
      if (!startUrl) return null

      const resp = await createWebsiteCrawlFetchFn({
        urlBuilderParams: { organizationId, workspaceId },
        jsonBody: {
          start_url: startUrl,
          crawl_config: payload.crawlConfig || {},
        },
      })
      if (createWebsiteCrawlError) throw createWebsiteCrawlError

      const createdFolder =
        resp?.data?.folder || createWebsiteCrawlResp?.data?.folder
      if (!createdFolder) return null

      const createdNode: KnowledgeNode = {
        id: createdFolder.id,
        knowledge_base_id: createdFolder.knowledge_base_id,
        parent_id: workspaceNodeIdStr,
        node_type: "FOLDER",
        name: createdFolder.name,
        source_type: createdFolder.source_type,
        created_at: createdFolder.created_at,
        updated_at: createdFolder.updated_at,
        children: [],
        children_loaded: false,
        ui_kind: "NODE" as const,
        workspace_id: workspaceId,
      }

      setRoot((prev) => {
        const next = updateNodeInTree(prev, workspaceNodeIdStr, (n) => {
          if (!isWorkspaceNode(n)) return n
          const existingChildren = n.children || []
          if (existingChildren.some((child) => child.id === createdNode.id)) {
            return n
          }
          return {
            ...n,
            children: [...existingChildren, createdNode],
            children_loaded: true,
          }
        })
        return next ?? prev
      })

      return createdNode
    },
    [
      createWebsiteCrawlFetchFn,
      createWebsiteCrawlError,
      createWebsiteCrawlResp?.data?.folder,
      findNodeById,
      getOrganizationIdForWorkspaceId,
      root,
      updateNodeInTree,
    ]
  )

  const deleteNode = useCallback(
    async (nodeId: string) => {
      const node = findNodeById(root, nodeId)
      if (!node) return
      if (isWorkspaceNode(node)) return
      const workspaceId = node.workspace_id
      if (!workspaceId) return
      const deletedNodeIds = collectKnowledgeBaseNodeIds(node)
      const organizationId = getOrganizationIdForWorkspaceId(workspaceId)
      if (!organizationId) return
      await deleteNodeFetchFn({
        urlBuilderParams: { organizationId, workspaceId, nodeId },
      })
      refreshKbStorageSummary()
      setRoot((prev) => {
        const next = updateNodeInTree(prev, nodeId, () => null)
        return next ?? prev
      })
      removeKnowledgeBaseNodeStatusesFromIndex(
        nodeStatusIndexRef.current,
        deletedNodeIds
      )
      setPendingNodeMap((prev) => {
        const next = new Map(prev)
        for (const deletedNodeId of deletedNodeIds) {
          next.delete(deletedNodeId)
        }
        return next
      })
      setSelectedNodeId((prev) => (prev === nodeId ? UI_ROOT_ID : prev))
    },
    [
      deleteNodeFetchFn,
      findNodeById,
      getOrganizationIdForWorkspaceId,
      refreshKbStorageSummary,
      root,
      updateNodeInTree,
    ]
  )

  const updateNode = useCallback(
    async (
      nodeId: string,
      patch: { name?: string; description?: string | null }
    ) => {
      const node = findNodeById(root, nodeId)
      if (!node) return
      if (isWorkspaceNode(node)) return
      const workspaceId = node.workspace_id
      if (!workspaceId) return
      const organizationId = getOrganizationIdForWorkspaceId(workspaceId)
      if (!organizationId) return

      const payload: { name?: string; description?: string | null } = {}
      if (patch.name !== undefined) payload.name = patch.name
      if (patch.description !== undefined)
        payload.description = patch.description
      if (payload.name === undefined && payload.description === undefined)
        return

      const resp = await updateNodeFetchFn({
        urlBuilderParams: { organizationId, workspaceId, nodeId },
        jsonBody: payload,
      })
      const updated = resp?.data
      if (!updated) return

      setRoot((prev) => {
        const next =
          updateNodeInTree(prev, nodeId, (n) => ({
            ...n,
            name: updated.name,
            description: updated.description ?? null,
            updated_at: updated.updated_at,
          })) ?? prev
        return next
      })
    },
    [
      findNodeById,
      getOrganizationIdForWorkspaceId,
      root,
      updateNodeFetchFn,
      updateNodeInTree,
    ]
  )

  const addFiles = useCallback(
    async (folderId: string, files: File[]) => {
      const folder = findNodeById(root, folderId)
      if (!folder || isWorkspaceNode(folder)) return
      const workspaceId = folder.workspace_id
      if (!workspaceId) return
      const organizationId = getOrganizationIdForWorkspaceId(workspaceId)
      if (!organizationId) return
      const kbId = getKbIdForNode(folder)
      if (!kbId) return
      if (isKbStorageAtLimitForWorkspaceId(workspaceId)) {
        showKbStorageLimitReached()
        return
      }
      if (files.length === 0) return

      let tabularCapabilities = null
      try {
        tabularCapabilities = (
          await getTabularCapabilitiesFetchFn({
            urlBuilderParams: { organizationId, workspaceId },
          })
        )?.data
      } catch (error) {
        console.error("[KB] tabular capabilities failed", error)
        alertToast({
          ...getUserFacingApiErrorToastProps(error, t, {
            title: t("knowledgeBase.treeView.uploadFile"),
            fallbackDescription: t(
              "knowledgeBase.treeView.tabular.loadCapabilitiesFailed"
            ),
          }),
        })
        return
      }

      const allowedMimeTypes = tabularCapabilities?.enabled
        ? [
            ...SUPPORTED_KB_MIME_TYPES,
            ...tabularCapabilities.accepted_mime_types,
          ]
        : SUPPORTED_KB_MIME_TYPES
      const supportedFiles: File[] = []
      const unsupportedFiles: File[] = []
      const invalidTabularFiles: File[] = []
      files.forEach((file) => {
        const normalizedFile = normalizeKnowledgeBaseUploadFile(
          file,
          allowedMimeTypes
        )
        if (!normalizedFile) {
          unsupportedFiles.push(file)
        } else if (
          tabularCapabilities?.enabled &&
          getTabularFileValidationFailure(normalizedFile, tabularCapabilities)
        ) {
          invalidTabularFiles.push(normalizedFile)
        } else {
          supportedFiles.push(normalizedFile)
        }
      })
      if (unsupportedFiles.length > 0) {
        alertToast({
          title: t("knowledgeBase.treeView.uploadFile"),
          description: t("knowledgeBase.treeView.unsupportedUpload", {
            files: unsupportedFiles.map((f) => f.name).join(", "),
          }),
        })
      }
      if (invalidTabularFiles.length > 0) {
        alertToast({
          title: t("knowledgeBase.treeView.uploadFile"),
          description: t("knowledgeBase.treeView.tabular.uploadValidation", {
            files: invalidTabularFiles.map((file) => file.name).join(", "),
          }),
        })
      }
      if (supportedFiles.length === 0) return
      if (supportedFiles.length > MAX_KB_UPLOAD_FILES) {
        alertToast({
          title: t("knowledgeBase.treeView.uploadFile"),
          description: t("common.fileUpload.error.maxFiles", {
            limit: MAX_KB_UPLOAD_FILES,
          }),
        })
        return
      }

      const form = new FormData()
      // Backend expects a multipart "form" field containing the UploadFilesForm schema.
      // See codeer/contexts/knowledge_base_api.py -> upload_files(form: UploadFilesForm, files: List[UploadedFile]).
      form.append("form", JSON.stringify({ parent_id: folderId }))
      for (const f of supportedFiles) form.append("files", f)
      const resp = await uploadFetchFn({
        urlBuilderParams: { organizationId, workspaceId, kbId },
        body: form,
      })
      refreshKbStorageSummary()
      const created = resp?.data?.nodes || []

      const failed = created.filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (n) => n.status === "FAILED" && (n as any).error_message
      ) as Array<{
        original_name?: string | null
        error_message?: string | null
      }>
      if (failed.length > 0) {
        const detail = failed
          .slice(0, 3)
          .map(
            (n) =>
              `${n.original_name || "unknown"}: ${n.error_message || "Upload failed"}`
          )
          .join("\n")
        alertToast({
          title: t("knowledgeBase.treeView.uploadFile"),
          description:
            failed.length > 3
              ? `${detail}\n...and ${failed.length - 3} more`
              : detail,
        })
      }

      // Refresh folder children to get real node rows (name, metadata)
      const nodesResp = await listNodesFetchFn({
        urlBuilderParams: { organizationId, workspaceId },
        searchParams: new URLSearchParams({
          parent_id: folderId,
        }),
      })
      const children = (nodesResp?.data || []).filter(
        (c) => c.node_type === "FILE"
      )
      mergeChildren(
        folderId,
        children.map((c) => ({
          ...c,
          parent_id: folderId,
          children: [],
          children_loaded: false,
          ui_kind: "NODE" as const,
          workspace_id: workspaceId,
        }))
      )

      setPendingNodeMap((prev) => {
        const next = new Map(prev)
        for (const n of created) {
          if (!n.node_id) continue
          if (hasPendingKnowledgeBaseStatus(n.status))
            next.set(n.node_id, { kbId, workspaceId })
        }
        return next
      })
    },
    [
      alertToast,
      findNodeById,
      getOrganizationIdForWorkspaceId,
      getTabularCapabilitiesFetchFn,
      isKbStorageAtLimitForWorkspaceId,
      listNodesFetchFn,
      mergeChildren,
      root,
      t,
      uploadFetchFn,
      refreshKbStorageSummary,
      showKbStorageLimitReached,
    ]
  )

  const addFromGoogleDrive = useCallback(
    async (folderId: string, connectionId: string) => {
      const folder = findNodeById(root, folderId)
      const workspaceId = folder?.workspace_id
      if (isKbStorageAtLimitForWorkspaceId(workspaceId)) {
        showKbStorageLimitReached()
        return
      }
      await googleDrive.addFromGoogleDrive(folderId, connectionId)
      refreshKbStorageSummary()
    },
    [
      findNodeById,
      googleDrive,
      isKbStorageAtLimitForWorkspaceId,
      refreshKbStorageSummary,
      root,
      showKbStorageLimitReached,
    ]
  )
  const connectAnotherGoogleAccount = useCallback(
    async (folderId: string) => {
      const folder = findNodeById(root, folderId)
      const workspaceId = folder?.workspace_id
      if (isKbStorageAtLimitForWorkspaceId(workspaceId)) {
        showKbStorageLimitReached()
        return
      }
      await googleDrive.connectAnotherGoogleAccount(folderId)
    },
    [
      findNodeById,
      googleDrive,
      isKbStorageAtLimitForWorkspaceId,
      root,
      showKbStorageLimitReached,
    ]
  )
  const addFromNotionConnection = useCallback(
    async (folderId: string, connectionId: string) => {
      const folder = findNodeById(root, folderId)
      const workspaceId = folder?.workspace_id
      if (isKbStorageAtLimitForWorkspaceId(workspaceId)) {
        showKbStorageLimitReached()
        return
      }
      await notion.addFromNotionConnection(folderId, connectionId)
      refreshKbStorageSummary()
    },
    [
      findNodeById,
      isKbStorageAtLimitForWorkspaceId,
      notion,
      refreshKbStorageSummary,
      root,
      showKbStorageLimitReached,
    ]
  )
  const connectAnotherNotionWorkspace = useCallback(
    async (folderId: string) => {
      const folder = findNodeById(root, folderId)
      const workspaceId = folder?.workspace_id
      if (isKbStorageAtLimitForWorkspaceId(workspaceId)) {
        showKbStorageLimitReached()
        return
      }
      await notion.connectAnotherNotionWorkspace(folderId)
    },
    [
      findNodeById,
      isKbStorageAtLimitForWorkspaceId,
      notion,
      root,
      showKbStorageLimitReached,
    ]
  )

  useEffect(() => {
    const nextPendingNodeIdsByWorkspace =
      collectPendingKnowledgeBaseNodeIdsByWorkspace(pendingNodeMap)
    pendingNodeIdsByWorkspaceRef.current = nextPendingNodeIdsByWorkspace
  }, [pendingNodeMap])

  const applyKnowledgeBasePollingSync = useCallback(
    ({
      items,
      resolvedNodeIds,
    }: {
      items: FileStatusItemSchema[]
      resolvedNodeIds: string[]
    }) => {
      if (items.length > 0) {
        setRoot((prev) => {
          for (const item of items) {
            if (!nodeStatusIndexRef.current.has(item.node_id)) continue
            nodeStatusIndexRef.current.set(item.node_id, item.status)
          }

          return applyKnowledgeBaseFileStatusUpdates(prev, items)
        })
      }

      if (resolvedNodeIds.length > 0) {
        setPendingNodeMap((prev) => {
          const next = removePendingKnowledgeBaseNodes(prev, resolvedNodeIds)
          pendingNodeIdsByWorkspaceRef.current =
            collectPendingKnowledgeBaseNodeIdsByWorkspace(next)
          return next
        })
      }
    },
    []
  )

  useEffect(() => {
    const controller = new KnowledgeBasePollingVisibilityController()
    const unsubscribe = controller.subscribe((isVisible) => {
      if (!isVisible) {
        clearAllPendingNodePollTimeouts()
        return
      }

      for (const [workspaceId] of pendingNodeIdsByWorkspaceRef.current) {
        const runtime = getWorkspacePollRuntime(workspaceId)
        if (runtime.inFlight) continue

        runtime.schedulerState = createKnowledgeBasePollingSchedulerState()
        schedulePendingNodePoll(workspaceId, 0)
      }
    })

    pollingVisibilityControllerRef.current = controller

    return () => {
      unsubscribe()
      controller.dispose()
      pollingVisibilityControllerRef.current = null
    }
  }, [
    clearAllPendingNodePollTimeouts,
    getWorkspacePollRuntime,
    schedulePendingNodePoll,
  ])

  const pollPendingNodes = useCallback(
    async (workspaceId: string) => {
      const workspaceNodeIds =
        pendingNodeIdsByWorkspaceRef.current.get(workspaceId) || []
      if (workspaceNodeIds.length === 0) {
        return { hadProgress: false, hadError: false }
      }

      const organizationId = getOrganizationIdForWorkspaceId(workspaceId)
      if (!organizationId) {
        return { hadProgress: false, hadError: true }
      }

      const baseStatusIndex = nodeStatusIndexRef.current
      const overlayStatuses: KnowledgeBaseNodeStatusIndex = new Map()
      const allItems: FileStatusItemSchema[] = []
      const resolvedNodeIds: string[] = []
      let hadProgress = false
      let hadError = false

      for (
        let index = 0;
        index < workspaceNodeIds.length;
        index += WORKSPACE_FILE_STATUS_BATCH_SIZE
      ) {
        const nodeIds = workspaceNodeIds.slice(
          index,
          index + WORKSPACE_FILE_STATUS_BATCH_SIZE
        )

        try {
          const resp = await fileStatusFetchFn({
            urlBuilderParams: { organizationId, workspaceId },
            jsonBody: { node_ids: nodeIds },
          })
          const items = resp?.data || []

          if (
            resolveKnowledgeBasePollingChunkProgress({
              baseStatuses: baseStatusIndex,
              overlayStatuses,
              requestedNodeIds: nodeIds,
              items,
            })
          ) {
            hadProgress = true
          }

          if (items.length > 0) {
            allItems.push(...items)
          }

          resolvedNodeIds.push(
            ...collectResolvedPendingKnowledgeBaseNodeIds(nodeIds, items)
          )
        } catch (error) {
          hadError = true
          console.error(
            `Knowledge base status polling failed for workspace ${workspaceId}:`,
            error
          )
        }
      }

      if (allItems.length > 0 || resolvedNodeIds.length > 0) {
        applyKnowledgeBasePollingSync({
          items: allItems,
          resolvedNodeIds,
        })
        refreshKbStorageSummary()
      }

      return { hadProgress, hadError }
    },
    [
      applyKnowledgeBasePollingSync,
      fileStatusFetchFn,
      getOrganizationIdForWorkspaceId,
      refreshKbStorageSummary,
    ]
  )

  const runPendingNodePollCycle = useCallback(
    async (workspaceId: string) => {
      const runtime = getWorkspacePollRuntime(workspaceId)
      if (runtime.inFlight) return
      if (!canPollWorkspace(workspaceId)) {
        clearPendingNodePollTimeout(workspaceId)
        return
      }

      const workspaceNodeIds =
        pendingNodeIdsByWorkspaceRef.current.get(workspaceId) || []
      if (workspaceNodeIds.length === 0) {
        removeWorkspacePollRuntime(workspaceId)
        return
      }

      runtime.inFlight = true
      let outcome = resolveKnowledgeBasePollingOutcome({
        hadProgress: false,
        hadError: false,
      })

      try {
        const result = await pollPendingNodes(workspaceId)
        outcome = resolveKnowledgeBasePollingOutcome(result)
      } catch (error) {
        outcome = "error"
        console.error(
          `Knowledge base polling cycle crashed for workspace ${workspaceId}:`,
          error
        )
      } finally {
        runtime.inFlight = false
      }

      if (
        (pendingNodeIdsByWorkspaceRef.current.get(workspaceId) || []).length ===
        0
      ) {
        removeWorkspacePollRuntime(workspaceId)
        return
      }

      runtime.schedulerState = advanceKnowledgeBasePollingScheduler(
        runtime.schedulerState,
        outcome
      )

      schedulePendingNodePoll(
        workspaceId,
        getKnowledgeBasePollingDelayMs(runtime.schedulerState)
      )
    },
    [
      canPollWorkspace,
      clearPendingNodePollTimeout,
      getWorkspacePollRuntime,
      pollPendingNodes,
      removeWorkspacePollRuntime,
      schedulePendingNodePoll,
    ]
  )

  runPendingNodePollCycleRef.current = runPendingNodePollCycle

  useEffect(() => {
    const previousPendingNodeIdsByWorkspace =
      previousPendingNodeIdsByWorkspaceRef.current
    const nextPendingNodeIdsByWorkspace = new Map<string, Set<string>>()

    for (const [
      workspaceId,
      nodeIds,
    ] of pendingNodeIdsByWorkspaceRef.current.entries()) {
      nextPendingNodeIdsByWorkspace.set(workspaceId, new Set(nodeIds))
    }

    previousPendingNodeIdsByWorkspaceRef.current = nextPendingNodeIdsByWorkspace

    for (const workspaceId of previousPendingNodeIdsByWorkspace.keys()) {
      if (nextPendingNodeIdsByWorkspace.has(workspaceId)) continue

      const runtime = workspacePollRuntimesRef.current.get(workspaceId)
      if (!runtime?.inFlight) {
        removeWorkspacePollRuntime(workspaceId)
        continue
      }

      clearPendingNodePollTimeout(workspaceId)
    }

    for (const [workspaceId, nextNodeIds] of nextPendingNodeIdsByWorkspace) {
      const runtime = getWorkspacePollRuntime(workspaceId)
      const previousNodeIds =
        previousPendingNodeIdsByWorkspace.get(workspaceId) || new Set<string>()
      const hasNewPendingNodes = Array.from(nextNodeIds).some(
        (nodeId) => !previousNodeIds.has(nodeId)
      )

      if (hasNewPendingNodes) {
        if (!runtime.inFlight && canPollWorkspace(workspaceId)) {
          runtime.schedulerState = createKnowledgeBasePollingSchedulerState()
          schedulePendingNodePoll(
            workspaceId,
            getKnowledgeBasePollingDelayMs(runtime.schedulerState)
          )
        }
        continue
      }

      if (
        runtime.timeoutId === null &&
        !runtime.inFlight &&
        canPollWorkspace(workspaceId)
      ) {
        schedulePendingNodePoll(
          workspaceId,
          getKnowledgeBasePollingDelayMs(runtime.schedulerState)
        )
      }
    }
  }, [
    canPollWorkspace,
    clearPendingNodePollTimeout,
    getWorkspacePollRuntime,
    pendingNodeMap,
    removeWorkspacePollRuntime,
    schedulePendingNodePoll,
  ])

  useEffect(() => {
    return () => {
      clearAllPendingNodePollTimeouts()
    }
  }, [clearAllPendingNodePollTimeouts])

  const fetchFileContent = useCallback(
    async (nodeId: string) => {
      if (contentInFlightRef.current.has(nodeId)) return
      const node = findNodeById(root, nodeId)
      if (!node || node.node_type !== "FILE") return
      if (node.content_loaded) return
      if (node.status !== "READY") return
      const workspaceId = node.workspace_id
      if (!workspaceId) return
      const organizationId = getOrganizationIdForWorkspaceId(workspaceId)
      if (!organizationId) return
      const kbId = getKbIdForNode(node)
      if (!kbId) return

      contentInFlightRef.current.add(nodeId)
      try {
        const resp = await fileContentFetchFn({
          urlBuilderParams: { organizationId, workspaceId, kbId, nodeId },
        })
        const content = resp?.data?.content ?? null
        setRoot((prev) => {
          const next =
            updateNodeInTree(prev, nodeId, (n) => ({
              ...n,
              content,
              content_loaded: true,
            })) ?? prev
          return next
        })
      } finally {
        contentInFlightRef.current.delete(nodeId)
      }
    },
    [
      fileContentFetchFn,
      findNodeById,
      getOrganizationIdForWorkspaceId,
      root,
      updateNodeInTree,
    ]
  )

  const fetchOriginalFile = useCallback(
    async (node: KnowledgeNode) => {
      const workspaceId = node.workspace_id!
      const url = buildKnowledgeBaseNodeMediaUrl({
        serverRoot: sys.serverRoot,
        organizationId: getOrganizationIdForWorkspaceId(workspaceId)!,
        workspaceId,
        knowledgeBaseId: getKbIdForNode(node)!,
        nodeId: node.id,
      }).replace(/\/media$/, "/download")
      const response = await ky.get(url, { credentials: "include" })
      const fileName = getOriginalDownloadFileName(response.headers)
      return { blob: await response.blob(), fileName }
    },
    [getOrganizationIdForWorkspaceId, sys.serverRoot]
  )

  const downloadFile = useCallback(
    async (node: KnowledgeNode) => {
      if (node.node_type !== "FILE") return
      if (node.status !== "READY") {
        alertToast({
          title: t("knowledgeBase.treeView.download"),
          description: "File is not ready for download.",
        })
        return
      }

      const kbId = getKbIdForNode(node)
      if (!kbId) return
      const workspaceId = node.workspace_id
      if (!workspaceId) return
      const organizationId = getOrganizationIdForWorkspaceId(workspaceId)
      if (!organizationId) return

      try {
        if (isUploadedFileNode(node)) {
          const { blob, fileName } = await fetchOriginalFile(node)
          saveAs(blob, fileName)
          return
        }
        // Fetch content from the snapshot object (context object)
        const resp = await fileContentFetchFn({
          urlBuilderParams: {
            organizationId,
            workspaceId,
            kbId,
            nodeId: node.id,
          },
        })

        const content = resp?.data?.content
        if (!content) {
          alertToast({
            title: t("knowledgeBase.treeView.download"),
            description: "No content available for download.",
          })
          return
        }

        // Get filename from API response (name field) or fallback to node name
        const fileName = resp?.data?.name || node.name || "file"
        const { safeBaseName, fileExtension, mimeType } =
          resolveFileDownloadMeta(node, fileName)

        // Create blob and trigger download
        const blob = new Blob([content], { type: mimeType })
        const blobUrl = window.URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.style.display = "none"
        a.href = blobUrl
        a.download = `${safeBaseName}${fileExtension}`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(blobUrl)
        document.body.removeChild(a)
      } catch (error) {
        console.error("[KB] download failed", error)
        alertToast({
          title: t("knowledgeBase.treeView.download"),
          description: getUserFacingApiErrorMessage(
            error,
            t,
            "Failed to download file."
          ),
        })
      }
    },
    [
      fileContentFetchFn,
      alertToast,
      fetchOriginalFile,
      getOrganizationIdForWorkspaceId,
      t,
    ]
  )

  const downloadFilesAsZip = useCallback(
    async (nodes: KnowledgeNode[]) => {
      const targetFiles = nodes.filter(
        (node) => node.node_type === "FILE" && node.status === "READY"
      )
      if (targetFiles.length === 0) return

      const zip = new JSZip()
      const usedNames = new Set<string>()
      let failedCount = 0
      const preparingToast = toast({
        title: t("knowledgeBase.treeView.download"),
        description: t("knowledgeBase.treeView.downloadZipPreparing"),
      })

      try {
        for (const node of targetFiles) {
          const kbId = getKbIdForNode(node)
          const workspaceId = node.workspace_id
          const organizationId = workspaceId
            ? getOrganizationIdForWorkspaceId(workspaceId)
            : null
          if (!kbId || !workspaceId || !organizationId) {
            failedCount += 1
            continue
          }

          try {
            let content: string | Blob | null = null
            let fileName = node.name || "file"
            if (isUploadedFileNode(node)) {
              const original = await fetchOriginalFile(node)
              content = original.blob
              fileName = original.fileName
            } else {
              const resp = await fileContentFetchFn({
                urlBuilderParams: {
                  organizationId,
                  workspaceId,
                  kbId,
                  nodeId: node.id,
                },
              })
              content = resp?.data?.content ?? null
              fileName = resp?.data?.name || fileName
            }
            if (!content) {
              failedCount += 1
              continue
            }

            const { safeBaseName, fileExtension } = resolveFileDownloadMeta(
              node,
              fileName
            )
            let resolvedName = `${safeBaseName}${fileExtension}`
            if (usedNames.has(resolvedName)) {
              let index = 1
              while (
                usedNames.has(`${safeBaseName} (${index})${fileExtension}`)
              ) {
                index += 1
              }
              resolvedName = `${safeBaseName} (${index})${fileExtension}`
            }
            usedNames.add(resolvedName)
            zip.file(resolvedName, content)
          } catch (error) {
            console.error("[KB] zip download failed for node", node.id, error)
            alertToast(
              getUserFacingApiErrorToastProps(error, t, {
                title: t("knowledgeBase.treeView.download"),
                fallbackDescription: "Failed to download file.",
              })
            )
            failedCount += 1
          }
        }

        if (usedNames.size === 0) {
          alertToast({
            title: t("knowledgeBase.treeView.download"),
            description: "No content available for download.",
          })
          return
        }

        const blob = await zip.generateAsync({ type: "blob" })
        saveAs(blob, buildZipFileName())

        if (failedCount > 0) {
          alertToast({
            title: t("knowledgeBase.treeView.download"),
            description: t("knowledgeBase.treeView.downloadZipPartialFailure", {
              count: failedCount,
            }),
          })
        } else {
          successToast({
            title: t("knowledgeBase.treeView.download"),
            description: t("knowledgeBase.treeView.downloadZipStarted"),
          })
        }
      } finally {
        preparingToast.dismiss()
      }
    },
    [
      alertToast,
      fileContentFetchFn,
      fetchOriginalFile,
      getOrganizationIdForWorkspaceId,
      successToast,
      t,
      toast,
    ]
  )

  const deleteConnection = useCallback(
    async (connectionId: string) => {
      try {
        await ky.delete(
          `${sys.serverRoot}${API_PREFIX}/connections/${connectionId}`,
          {
            credentials: "include",
            retry: 0,
            timeout: 60_000,
          }
        )
        // Refresh connections after deletion
        refreshConnections()
      } catch (error) {
        console.error("[KB] deleteConnection failed", error)
        alertToast({
          ...getUserFacingApiErrorToastProps(error, t, {
            title: t("connection.messages.deleteFailed.title"),
            fallbackDescription: t("connection.messages.deleteFailed.title"),
          }),
        })
        throw error
      }
    },
    [sys.serverRoot, refreshConnections, alertToast, t]
  )

  return {
    // Core tree
    root,
    totalItems,

    // Persistent UI state
    selectedNode,
    selectNode,

    createFolder,
    createWebsiteCrawl,
    deleteNode,
    updateNode,
    addFiles,
    addFromGoogleDrive,
    connectAnotherGoogleAccount,
    addFromNotionConnection,
    connectAnotherNotionWorkspace,
    deleteConnection,
    connections,
    knowledgeBaseStorage,
    isKbStorageAtLimit,
    isKbStorageAtLimitForWorkspaceId,
    workspaces,
    ensureChildrenLoaded,
    refreshFolderChildren,
    fetchFileContent,
    getFileMediaUrl,
    downloadFile,
    downloadFilesAsZip,
  }
}

export default useKnowledgeBase
